import type { RoleAssignmentScopeInput } from '@/lib/api/types';

/*
  The state and the rules behind the scope picker, with no React in them.

  Split out of ScopePicker.tsx rather than living beside the component, for the same reason
  device-search.ts sits beside DevicesPage: this is the part with logic worth reading on its own,
  and a module that exports both a component and its helpers cannot be hot reloaded.

  The shape of the problem: the schema constraint ck_role_assignments_single_scope allows at most
  one scope node, so the finished value is the RoleAssignmentScopeInput union. But the form has
  intermediate states the union deliberately cannot express, such as "site level, no site chosen
  yet". ScopeSelection is that wider state, and toScopeInput is the narrowing, returning null
  while the selection is incomplete.
*/

export type ScopeLevel = 'tenant' | 'organization' | 'site' | 'device_group' | 'device';

export const SCOPE_LEVELS: readonly { readonly value: ScopeLevel; readonly label: string }[] = [
  { value: 'tenant', label: 'Tenant wide (every customer, every device)' },
  { value: 'organization', label: 'One organization' },
  { value: 'site', label: 'One site' },
  { value: 'device_group', label: 'One device group' },
  { value: 'device', label: 'A single device' },
];

export const SCOPE_LEVEL_HELP: Readonly<Record<ScopeLevel, string>> = {
  tenant:
    'The broadest grant there is. Every organization, present and future, and every device in them. Reserve it for administrators.',
  organization: 'Everything belonging to one customer, including sites and groups added later.',
  site: 'One physical location, and every group and device at it.',
  device_group:
    'One group of devices. The narrowest scope that still covers a set rather than a single machine.',
  device: 'Exactly one machine. Useful for a contractor brought in for one problem.',
};

export interface ScopeSelection {
  readonly level: ScopeLevel;
  readonly organizationId: string | null;
  readonly siteId: string | null;
  readonly deviceGroupId: string | null;
  readonly deviceId: string | null;
}

/**
 * The starting point for a new grant.
 *
 * The level defaults to organization rather than tenant on purpose. Tenant wide is the most
 * powerful grant available and should be a decision someone makes, not the value they get by
 * leaving a control alone. docs/07-identity-rbac.md I.2 puts "narrow scope" in the built-in role
 * table for the same reason.
 */
export const EMPTY_SCOPE_SELECTION: ScopeSelection = {
  level: 'organization',
  organizationId: null,
  siteId: null,
  deviceGroupId: null,
  deviceId: null,
};

/** The valid scope for a selection, or null while it is still incomplete. */
export function toScopeInput(selection: ScopeSelection): RoleAssignmentScopeInput | null {
  switch (selection.level) {
    case 'tenant':
      return { kind: 'tenant' };
    case 'organization':
      return selection.organizationId === null
        ? null
        : { kind: 'organization', organizationId: selection.organizationId };
    case 'site':
      return selection.siteId === null ? null : { kind: 'site', siteId: selection.siteId };
    case 'device_group':
      return selection.deviceGroupId === null
        ? null
        : { kind: 'device_group', deviceGroupId: selection.deviceGroupId };
    case 'device':
      return selection.deviceId === null ? null : { kind: 'device', deviceId: selection.deviceId };
  }
}

/**
 * What is still missing, phrased as an instruction rather than as a validation message. Null when
 * the selection is complete.
 *
 * Note that a device group scope does not require a site: an organization wide group has none, so
 * demanding one would make those groups unselectable.
 */
export function scopeSelectionGap(selection: ScopeSelection): string | null {
  if (selection.level === 'tenant') {
    return null;
  }
  if (selection.organizationId === null) {
    return 'Choose an organization.';
  }
  if (selection.level === 'organization') {
    return null;
  }
  if (selection.level === 'site') {
    return selection.siteId === null ? 'Choose a site.' : null;
  }
  if (selection.level === 'device_group') {
    return selection.deviceGroupId === null ? 'Choose a device group.' : null;
  }
  return selection.deviceId === null ? 'Choose a device.' : null;
}
